'use client';

import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { type ReactElement, useContext, useEffect, useRef } from 'react';

import { CLICK_MARGIN, NAV_HEIGHT } from '../measurements';
import { NavigationTabsContext } from '../navigation-context';

export type Props = {
  // Titles that are missing or start with "*" render without a navigation tab
  tabTitle?: string | null;
  withExtraMargin?: boolean;
} & BoxProps;

// Wraps one section of a NavigationTabs page. It owns the element the
// secondary navigation scrolls to and registers itself as a tab, which leaves
// its own children opaque so they can be async server components.
export const NavigationTarget = (props: Props): ReactElement => {
  const { children, tabTitle, withExtraMargin, ...boxProps } = {
    tabTitle: null,
    withExtraMargin: true,
    ...props,
  };

  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(NavigationTabsContext);

  const title = tabTitle && !tabTitle.startsWith('*') ? tabTitle : null;

  useEffect(() => {
    if (!context || !title) return;

    context.register({ ref, title });

    return () => context.unregister(title);
  }, [context, title]);

  const extraMargin = withExtraMargin ? CLICK_MARGIN : 0;

  return (
    <Box
      ref={ref}
      scrollMarginTop={`${NAV_HEIGHT + extraMargin}px`}
      {...boxProps}
    >
      {children}
    </Box>
  );
};
